/**
 * Module dependencies.
 */

import { ModelRangeProps } from 'src/types/model-ranges';
import { availableFiltersKeys } from 'src/core/content-config/model-ranges';
import { fetchModelRanges, paginationLimit } from './fetch-model-ranges';
import { useInfiniteQuery } from 'react-query';
import { useRouter } from 'next/router';

/**
 * `Data` type.
 */

type Data = {
  data: ModelRangeProps[];
  nextPage: number | null;
  total: number;
};

/**
 * Export `InitialData` type.
 */

export type InitialData = {
  pageParams: number[];
  pages: Data[];
};

/**
 * `Options` type.
 */

type Options = {
  filterParams: any;
  initialData?: InitialData | null;
  sortParam?: string;
  technologyId: string;
};

/**
 * Action type.
 */

const actionType = ({ locale, technologyId }): string => {
  return `${locale}-model-ranges-technology-${technologyId}`;
};

/**
 * `useModelRanges` hook.
 */

function useModelRanges(options?: Options) {
  const { filterParams, initialData, sortParam, technologyId } = options;
  const { locale } = useRouter();
  const result = useInfiniteQuery<Data>(
    actionType({ locale, technologyId }),
    ({ pageParam = 1 }) =>
      fetchModelRanges({
        locale,
        params: {
          filter: filterParams,
          limit: paginationLimit,
          page: pageParam,
          sort: sortParam ?? availableFiltersKeys.waterCapacity
        },
        technologyId
      }),
    {
      getNextPageParam: (lastPage, allPages) => {
        const pageLimit = Math.ceil(allPages[0]?.total / paginationLimit);
        const nextPage = lastPage?.nextPage;

        return nextPage <= pageLimit ? nextPage : false;
      },
      initialData
    }
  );

  return result;
}

/**
 * Export `useModelRanges` hook.
 */

export default useModelRanges;
